// Sesión de la consola: la persona (Supabase Auth), los negocios donde puede entrar al gateway y el elegido.
// Un implementador cambia de negocio desde el menú; todo lo demás se recarga con el negocio nuevo.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fijarCuenta } from './api';
import { supabase } from './supabase';

export interface Cuenta { cuenta_id: string; cuenta: string; persona: string; rol: 'administrador' | 'implementador' | 'administracion'; puedeAdministrar: boolean }
interface Yo { usuario: { id: string; correo: string | null }; cuentas: Cuenta[] }

interface ValorSesion {
  sesion: Session | null;
  listo: boolean;
  yo: Yo | undefined;
  cuenta: Cuenta | null;
  elegirCuenta: (id: string) => void;
  salir: () => Promise<void>;
  aviso: (texto: string) => void;
  toast: { id: number; texto: string } | null;
}

const Contexto = createContext<ValorSesion | null>(null);
const CLAVE = 'glubi.gateway.cuenta';
const leerGuardada = () => { try { return localStorage.getItem(CLAVE); } catch { return null; } };

export function SesionProvider({ children }: { children: ReactNode }) {
  const [sesion, setSesion] = useState<Session | null>(null);
  const [listo, setListo] = useState(false);
  const [elegida, setElegida] = useState<string | null>(leerGuardada);
  const [toast, setToast] = useState<{ id: number; texto: string } | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSesion(data.session); setListo(true); });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSesion(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const yo = useQuery({ queryKey: ['yo', sesion?.user.id], queryFn: () => api<Yo>('/api/yo'), enabled: Boolean(sesion) });
  const cuenta = useMemo(() => {
    const lista = yo.data?.cuentas ?? [];
    return lista.find((c) => c.cuenta_id === elegida) ?? lista[0] ?? null;
  }, [yo.data, elegida]);
  fijarCuenta(cuenta?.cuenta_id ?? null);

  const elegirCuenta = useCallback((id: string) => {
    try { localStorage.setItem(CLAVE, id); } catch { /* modo privado */ }
    fijarCuenta(id);
    setElegida(id);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'yo' && q.queryKey[0] !== 'negocios' });
  }, [qc]);

  const aviso = useCallback((texto: string) => {
    const id = Date.now();
    setToast({ id, texto });
    setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 4200);
  }, []);

  const salir = useCallback(async () => { await supabase.auth.signOut(); qc.clear(); }, [qc]);

  return <Contexto.Provider value={{ sesion, listo, yo: yo.data, cuenta, elegirCuenta, salir, aviso, toast }}>{children}</Contexto.Provider>;
}

export function useSesion(): ValorSesion {
  const v = useContext(Contexto);
  if (!v) throw new Error('Falta SesionProvider');
  return v;
}
